import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  Grid,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { useUserRole } from "../../hook/useUserRole.js";
import {
  getUserDetailsApi,
  updateUserDetailsApi,
  reactiveUserApi,
  disableUserApi,
} from "../../api/user.js";
import { getCompanysApi } from "../../api/company";
import { updatePermissionApi } from "../../api/member";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import { toLocal } from "../../utils/date.js";
import MainTable from "../../component/table/MainTable.js";
import SelectComponent from "../../component/SelectComponent.js";
import AddNewCompanyPopup from "./components/AddNewCompanyPopup.js";
import ConfirmPopup from "../../component/popup/ConfirmPopup.js";

const typographyStyle = {
  color: "#333",
  py: 1,
  fontWeight: 700,
  fontSize: "12px",
  minWidth: 120,
};

const tableStyle = {
  height: "calc(100% - 52px)",
  maxHeight: "calc(100% - 52px)",
  ".MuiButtonBase-root.MuiTableSortLabel-root.Mui-active": {
    color: "#fff",
  },
  ".MuiButtonBase-root.MuiTableSortLabel-root:hover": {
    color: "#fff",
  },
  ".MuiTableSortLabel-icon": {
    color: "#fff!important",
    opacity: "1!important",
  },
};

const textFieldStyle = {
  my: 1,
  ml: 1,
  flex: 1,
  borderRadius: "4px",
  background: "#dbe1eb",
  "& .MuiInputBase-input": {
    py: 0.5,
    px: 0,
  },
  "& .MuiInputBase-root": {
    px: 2,
    py: 0,
  },
};

const buttonStyle = {
  lineHeight: "normal",
  my: 2,
  borderRadius: "10px",
};

const UserDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const userRoleOptions = useUserRole();
  const renderTextField = (label, field, placeholder, disabled = false) => (
    <Grid item xs={5} sx={{ display: "flex", alignItems: "center" }}>
      <Typography sx={typographyStyle}>{label}</Typography>
      {disabled ? (
        <Typography sx={{ ...typographyStyle, fontWeight: 500, ml: 1.5 }}>
          {state[field]}
        </Typography>
      ) : (
        <TextField
          value={state[field]}
          onChange={(e) => updateState(field, e.target.value)}
          placeholder={placeholder}
          variant="outlined"
          sx={textFieldStyle}
          inputProps={{ maxLength: 200 }}
        />
      )}
    </Grid>
  );

  const userLogTableHead = [
    { id: "timestamp", label: "Date", align: "left", type: "dateWithTime" },
    { id: "project", label: "Project", align: "left" },
    { id: "action", label: "Action", align: "left" },
  ];

  const assignedProjectsTableHead = [
    { id: "projectName", label: "Project Name", align: "left" },
    { id: "projectNumber", label: "Project Number", align: "left" },
    { id: "accessLevel", label: "Access Level", align: "left" },
    { id: "editRight", label: "Edit Right", align: "center", type: "switch" },
    {
      id: "reportModule",
      label: "Report Module",
      align: "center",
      type: "switch",
    },
    { id: "costAccess", label: "Cost Module", align: "center", type: "switch" },
    { id: "addedOn", label: "Added on", align: "left", type: "date" },
    { id: "status", label: "Project Status", align: "left" },
  ];

  const [state, setState] = useState({
    firstName: "",
    lastName: "",
    email: "",
    position: "",
    lastLogin: "",
    companyOptions: [],
    companyValue: "",
    userRoleValue: "",
    userLogTableData: [],
    assignedProjectsTableData: [],
    editUserDetails: false,
    originalState: {
      firstName: "",
      lastName: "",
    },
    pendingSwitchUpdate: {},
    isLoading: false,
    logPage: 0,
    logRowsPerPage: 20,
    projectPage: 0,
    projectRowsPerPage: 20,
  });
  const [addNewCompanyPopup, setAddNewCompanyPopup] = useState(false);
  const [changeUserStatusPopup, setChangeUserStatusPopup] = useState(false);
  const [updatePermissionPopup, setUpdatePermissionPopup] = useState(false);
  const [changeStatusMessage, setChangeStatusMessage] = useState(null);
  const [updatePermissionMessage, setUpdatePermissionMessage] = useState(null);
  const [filteredUserRoleOptions, setFilteredUserRoleOptions] = useState([]);

  const updateState = (field, value) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    return (
      state.firstName !== "" &&
      state.lastName !== "" &&
      state.email !== "" &&
      state.userRoleValue !== "" &&
      state.companyValue !== ""
    );
  };

  const getUserDetailsData = async () => {
    try {
      updateState("isLoading", true);
      const result = await getUserDetailsApi(projectId);
      if (result?.data) {
        const updatedState = {
          ...result.data,
          lastLogin: result.data.lastLogin
            ? toLocal(result.data.lastLogin).format("DD/MM/YYYY HH:mm")
            : "",
          userRole:
            userRoleOptions?.find(
              (option) => option.label === result.data.userRole
            )?.label || result.data.userRole,
          userRoleValue:
            userRoleOptions?.find(
              (option) => option.label === result.data.userRole
            )?.value || result.data.userRole,
          companyValue:
            state.companyOptions?.find(
              (option) => option.label === result.data.company
            )?.value || result.data.company,
        };
        const assignedProjectsTableData = result.data?.projects?.map(
          (item) => ({
            ...item,
            id: item.projectUserId,
          })
        );
        const userLogTableData = result.data?.notifications?.map((item) => ({
          ...item,
        }));
        setState((prevState) => ({
          ...prevState,
          ...updatedState,
          originalState: updatedState,
          assignedProjectsTableData: assignedProjectsTableData,
          userLogTableData: userLogTableData,
        }));
      }
    } catch {
      showError("Received user details data occurred error.");
    } finally {
      updateState("isLoading", false);
    }
  };

  const getCompanyData = async () => {
    try {
      updateState("isLoading", true);
      const result = await getCompanysApi();
      if (result?.data) {
        updateState("companyOptions", result.data);
      }
    } catch {
      showError("Received company data occurred error.");
    } finally {
      updateState("isLoading", false);
    }
  };

  //update user details
  const updateUserDetails = async () => {
    const params = {
      userId: state.userId,
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
      userRole: state.userRoleValue,
      position: state.position,
      company: state.companyValue,
    };
    try {
      const result = await updateUserDetailsApi(params);
      if (result?.data) {
        showSuccess("The user has been updated.");
        getUserDetailsData();
        setState((prevState) => ({
          ...prevState,
          editUserDetails: !prevState.editUserDetails,
        }));
      }
    } catch {
      showError("The user update failed.");
    }
  };

  //disable user
  const confirmDisableUser = async () => {
    try {
      const result = await disableUserApi(projectId);
      if (result?.data) {
        showSuccess("The account has been disabled.");
        getUserDetailsData();
      }
    } catch {
      showError("The account disable failed.");
    } finally {
      setChangeUserStatusPopup(false);
    }
  };

  //reactive user
  const confirmReactiveUser = async () => {
    try {
      const result = await reactiveUserApi(projectId);
      if (result?.data) {
        showSuccess("The account has been reactived.");
        getUserDetailsData();
      }
    } catch {
      showError("The account reactivated failed.");
    } finally {
      setChangeUserStatusPopup(false);
    }
  };

  //confirm update permission
  const confirmUpdatePermission = async () => {
    const { rowId, columnName, switchValue, originalRow } =
      state.pendingSwitchUpdate || {};

    if (!rowId || !columnName || !originalRow || switchValue === undefined) {
      showError("The project Access update occured error.");
      setUpdatePermissionPopup(false);
      return;
    }
    try {
      const updatedRow = {
        ...originalRow,
        [columnName]: switchValue,
      };
      const result = await updatePermissionApi(
        updatedRow.projectId,
        updatedRow.projectUserId,
        updatedRow.accessLevel === "Project Member" ? 1 : 2,
        updatedRow.editRight,
        updatedRow.reportModule,
        updatedRow.costAccess
      );

      if (result?.data) {
        const updatedAssignedProjects = state.assignedProjectsTableData.map(
          (row) =>
            row.id === rowId ? { ...row, [columnName]: switchValue } : row
        );
        updateState("assignedProjectsTableData", updatedAssignedProjects);
        updateState("pendingSwitchUpdate", null);
        showSuccess("The project Access has been updated successfully.");
      }
    } catch {
      showError("The project Access update failed.");
    } finally {
      setUpdatePermissionPopup(false);
    }
  };

  const filterUserRolesByCompany = (isClpCompany, roles) => {
    if (!roles) return roles;
    if (isClpCompany) {
      return roles.filter((role) => role.value !== 4);//排除Contractor
    } else {
      return roles.filter((role) => [1, 4].includes(role.value));//System Administrator 和 Contractor
    }
  };

  useEffect(() => {
    const isClpCompany = state.companyValue === 2; //CLP
    const newFilteredRoles = filterUserRolesByCompany(
      isClpCompany,
      userRoleOptions
    );
    setFilteredUserRoleOptions(newFilteredRoles);
  }, [state.companyValue, userRoleOptions]);

  useEffect(() => {
    if (!projectId || !userRoleOptions) return;
    getUserDetailsData();
  }, [projectId, userRoleOptions, state.companyOptions]);

  useEffect(() => {
    getCompanyData();
  }, []);

  return (
    <Box sx={{ minWidth: 1100 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          User Profile Management
          <img
            src="assets/icons/greytwopeople.svg"
            alt="Project"
            style={{
              width: "18px",
              height: "22px",
              marginLeft: "8px",
              verticalAlign: "text-top",
            }}
          />
        </Typography>
        <Button
          variant="outlined"
          color="inherit"
          sx={{ ...buttonStyle, my: 0 }}
          onClick={() => {
            navigate("/modules/UserManagement");
          }}
        >
          Back
        </Button>
      </Box>
      <Box
        sx={{
          p: 2,
          mt: 1,
          height: "calc(50vh - 190px)",
          minHeight: 250,
          background: "#D0D5DE",
          borderRadius: "10px",
        }}
      >
        {state.isLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                User Profile -{" "}
                {state.originalState?.firstName +
                  " " +
                  state.originalState?.lastName}
              </Typography>
              <Box>
                {!state.editUserDetails && (
                  <>
                    {state.status === "Active" || state.status === "Pending" ? (
                      <Button
                        onClick={() => {
                          setChangeUserStatusPopup(true);
                          setChangeStatusMessage(
                            `Are you sure to disable ${state.originalState?.firstName +
                            " " +
                            state.originalState?.lastName
                            }?`
                          );
                        }}
                        variant="contained"
                        color="error"
                        sx={{ ...buttonStyle, my: 0, mr: 1 }}
                      >
                        Disable User
                      </Button>
                    ) : state.status === "Disable" ? (
                      <Button
                        onClick={() => {
                          setChangeUserStatusPopup(true);
                          setChangeStatusMessage(
                            `Are you sure to reactive ${state.originalState?.firstName +
                            " " +
                            state.originalState?.lastName
                            }?`
                          );
                        }}
                        variant="contained"
                        color="error"
                        sx={{ ...buttonStyle, my: 0, mr: 1 }}
                      >
                        Reactive User
                      </Button>
                    ) : (
                      <></>
                    )}
                    <Button
                      onClick={() => {
                        setState((prevState) => ({
                          ...prevState,
                          editUserDetails: !prevState.editUserDetails,
                        }));
                      }}
                      variant="contained"
                      color="success"
                      sx={{ ...buttonStyle, my: 0, mr: 1 }}
                    >
                      Update
                    </Button>
                  </>
                )}
                {state.editUserDetails && (
                  <>
                    <Button
                      onClick={updateUserDetails}
                      variant="contained"
                      disabled={!isFormValid()}
                      color={isFormValid() ? "success" : "primary"}
                      sx={{ ...buttonStyle, my: 0, mr: 1 }}
                    >
                      Save
                    </Button>
                    <Button
                      onClick={() => {
                        setState((prevState) => ({
                          ...prevState,
                          ...prevState.originalState,
                          editUserDetails: false,
                        }));
                      }}
                      variant="outlined"
                      color="inherit"
                      sx={{ ...buttonStyle, my: 0, mr: 1 }}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Box>
            <Box sx={{ py: 2 }}>
              <Grid container columnSpacing={3}>
                {renderTextField(
                  "First Name: ",
                  "firstName",
                  "Please Enter First Name...",
                  !state.editUserDetails
                )}
                <Grid item xs={5} display="flex" alignItems="center">
                  {!state.editUserDetails ? (
                    renderTextField(
                      "User Role:",
                      "userRole",
                      "",
                      !state.editUserDetails
                    )
                  ) : (
                    <SelectComponent
                      // options={userRoleOptions || []}
                      options={filteredUserRoleOptions}
                      value={state.userRoleValue}
                      onChange={(value) => {
                        updateState("userRoleValue", value);
                      }}
                      selectLabel="User Role:"
                      labelStyle={{
                        fontSize: "12px",
                        color: "#333",
                        minWidth: 120,
                        mr: 1,
                      }}
                      selectStyle={{ width: "100%", background: "#dbe1eb" }}
                      placeholder="Please Select User Role..."
                    />
                  )}
                </Grid>
                {renderTextField(
                  "Last Name:",
                  "lastName",
                  "Please Enter Last Name...",
                  !state.editUserDetails
                )}
                {renderTextField(
                  "Position:",
                  "position",
                  "Please Enter Position...",
                  !state.editUserDetails
                )}
                {renderTextField(
                  "Email Address:",
                  "email",
                  "Please Enter Email Address...",
                  !state.editUserDetails
                )}
                <Grid item xs={5} display="flex" alignItems="center">
                  {!state.editUserDetails ? (
                    renderTextField(
                      "Company:",
                      "company",
                      "",
                      !state.editUserDetails
                    )
                  ) : (
                    <SelectComponent
                      options={state.companyOptions}
                      value={state.companyValue}
                      onChange={(value) => {
                        updateState("companyValue", value);
                        updateState("userRoleValue", "");
                      }}
                      selectLabel="Company:"
                      labelStyle={{
                        fontSize: "12px",
                        color: "#333",
                        minWidth: 120,
                        mr: 1,
                      }}
                      selectStyle={{ width: "100%", background: "#dbe1eb" }}
                      placeholder="Please Select Company:..."
                    />
                  )}
                </Grid>
                {state.editUserDetails && (
                  <Grid item xs={2} display="flex" alignItems="center">
                    <Button
                      onClick={(event) => {
                        event.preventDefault();
                        setAddNewCompanyPopup(true);
                      }}
                      type="submit"
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: "10px", lineHeight: "normal" }}
                    >
                      Add Company
                    </Button>
                  </Grid>
                )}
                {renderTextField("Last Login: ", "lastLogin", "", true)}
                <Grid item xs={5} display="flex" alignItems="center">
                  <Typography sx={typographyStyle}>Account Status:</Typography>
                  <Box
                    sx={{
                      display: "inline-block",
                      width: "15px",
                      height: "15px",
                      minWidth: "15px",
                      verticalAlign: "text-bottom",
                      margin: "0 6px 2px 12px",
                      backgroundColor:
                        state.status === "Active"
                          ? "#00B176"
                          : state.status === "Pending"
                            ? "#fff"
                            : state.status === "Disable"
                              ? "#FF0303"
                              : "#A2ACA8",
                    }}
                  ></Box>
                  <Typography sx={{ ...typographyStyle, fontWeight: 500 }}>
                    {state.status}
                  </Typography>
                </Grid>
                <Grid item xs={5}></Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
      <Box
        sx={{
          height: "53vh",
          mt: 2,
          borderRadius: "10px",
          display: "flex",
          gap: 2,
        }}
      >
        <Box
          sx={{
            border: "1px solid #D0D5DE",
            p: 2,
            borderRadius: "10px",
            flex: 1,
            width: "50%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
              User Activity Log
            </Typography>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />
          <MainTable
            paperSx={{
              height: "calc(50vh - 55px)",
            }}
            sx={tableStyle}
            columns={userLogTableHead}
            rows={state.userLogTableData}
            headerStyles={{
              default: {
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
                color: "#fff",
              },
            }}
            sortable
            sortableColumns={["timestamp"]}
            showPagination
            page={state.logPage}
            rowsPerPage={state.logRowsPerPage}
            onPageChange={(newPage) =>
              setState((prevState) => ({ ...prevState, logPage: newPage }))
            }
            onRowsPerPageChange={(newRowsPerPage) =>
              setState((prevState) => ({
                ...prevState,
                logRowsPerPage: newRowsPerPage,
              }))
            }
            isLoading={state.isLoading}
          />
        </Box>
        <Box
          sx={{
            border: "1px solid #D0D5DE",
            p: 2,
            borderRadius: "10px",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
              Assigned Projects
            </Typography>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />
          <MainTable
            paperSx={{
              height: "calc(50vh - 55px)",
            }}
            sx={tableStyle}
            columns={assignedProjectsTableHead}
            rows={state.assignedProjectsTableData}
            headerStyles={{
              default: {
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
                color: "#fff",
              },
            }}
            sortable
            sortableColumns={["addedOn"]}
            showPagination
            page={state.projectPage}
            rowsPerPage={state.projectRowsPerPage}
            onPageChange={(newPage) =>
              setState((prevState) => ({ ...prevState, projectPage: newPage }))
            }
            onRowsPerPageChange={(newRowsPerPage) =>
              setState((prevState) => ({
                ...prevState,
                projectRowsPerPage: newRowsPerPage,
              }))
            }
            onSwitchChange={(rowId, columnName, switchValue) => {
              const targetRow = state.assignedProjectsTableData.find(
                (row) => row.id === rowId
              );
              const NameMaps = {
                costAccess: "Cost Module",
                reportModule: "Report Module",
                editRight: "Edit Right",
              };
              const Name = NameMaps[columnName] || columnName;
              setUpdatePermissionPopup(true);
              setUpdatePermissionMessage(
                `Are you sure you want to update the ${Name} Access of ${targetRow.projectName}?`
              );
              updateState("pendingSwitchUpdate", {
                rowId,
                columnName,
                switchValue,
                originalRow: targetRow,
              });
            }}
            isLoading={state.isLoading}
          />
        </Box>
      </Box>
      <AddNewCompanyPopup
        open={addNewCompanyPopup}
        onClose={() => setAddNewCompanyPopup(false)}
        options={state.companyOptions}
        onSetSuccess={(value) => {
          getCompanyData();
          showSuccess(value);
        }}
        onSetError={(value) => {
          showError(value);
        }}
      />
      <ConfirmPopup
        open={changeUserStatusPopup}
        message={changeStatusMessage}
        type="error"
        buttonLabelText="No"
        proceedButtonLabelText="Yes"
        iconSrc="assets/icons/error.svg"
        onCancel={() => {
          setChangeUserStatusPopup(false);
        }}
        onConfirm={() => {
          if (state.status === "Active" || state.status === "Pending") {
            confirmDisableUser();
          } else if (state.status === "Disable") {
            confirmReactiveUser();
          }
        }}
      ></ConfirmPopup>
      <ConfirmPopup
        open={updatePermissionPopup}
        message={updatePermissionMessage}
        type="error"
        buttonLabelText="No"
        proceedButtonLabelText="Yes"
        iconSrc="assets/icons/error.svg"
        onCancel={() => {
          setUpdatePermissionPopup(false);
        }}
        onConfirm={confirmUpdatePermission}
      ></ConfirmPopup>
    </Box>
  );
};

export default UserDetails;
